import {
  Badge,
  BadgeProps,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { P2PConfig, P2PPost } from "../../models/p2p";
import { formatNumber } from "../../utils/formatNumber";
import { useAccount } from "../../models/account";
import { useState } from "react";
import { format } from "date-fns";

export const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -20,
  },
}));

const PostCard = ({
  highlighted,
  post,
  config,
  onButtonClick,
  isMobile,
  buttonText,
  buttonBadge,
  buttonDisabledText,
  onAltButtonClick,
  altButtonText,
  altButtonDisabledText,
}: {
  highlighted?: boolean;
  post?: P2PPost;
  config?: P2PConfig;
  isMobile?: boolean;
  onButtonClick?: () => void;
  buttonText?: string;
  buttonBadge?: string | number;
  buttonDisabledText?: string;
  onAltButtonClick?: () => void;
  altButtonText?: string;
  altButtonDisabledText?: string;
}) => {
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [altLoading, setAltLoading] = useState(false);

  const deal =
    (post?.deals || []).find((d) => d.customer === account.address) ||
    (post?.dealer === account.address
      ? (post?.deals || []).find((d) => d.state === "dispute")
      : undefined);
  const expiry = 1000 * Number((deal || post)?.expiry || 0);

  return post ? (
    <Card
      variant="outlined"
      sx={(t) => ({
        bgcolor: t.palette.background.default,
        mb: 4,
        borderColor: highlighted ? t.palette.primary.main : undefined,
      })}
    >
      <CardContent sx={{ position: "relative" }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography
            gutterBottom
            sx={{ color: "text.secondary", fontSize: 14 }}
          >
            {isMobile
              ? post.dealer.slice(0, 8) + "..." + post.dealer.slice(-8)
              : post.dealer}
          </Typography>
          <Stack direction="row" gap={2}>
            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Trade ID: {post.post_id}
            </Typography>
            <Chip
              sx={{ textTransform: "capitalize" }}
              size="small"
              variant="outlined"
              label={(deal || post).state
                .replace("_bank", "")
                .replace(/_/g, " ")}
              color={(deal || post).state === "dispute" ? "error" : "success"}
            />
          </Stack>
        </Stack>
        <Typography variant="h5" component="div" mb={2}>
          {formatNumber(post.settle_price / 10 ** (config?.decimals || 6))}{" "}
          {post.settle_currency.currency}
          <Typography variant="body1" component="span">
            {" / "}
            {config?.name || ""}
          </Typography>
        </Typography>

        <Typography variant="body2" gutterBottom>
          Available Amount:{" "}
          <b>
            {formatNumber(post.amount / 10 ** (config?.decimals || 6))}{" "}
            {config?.name}
          </b>
        </Typography>
        <Typography variant="body2" gutterBottom>
          Minimum Amount:{" "}
          <b>
            {formatNumber(post.min_amount / 10 ** (config?.decimals || 6))}{" "}
            {config?.name}
          </b>
        </Typography>
        <Typography variant="body2" gutterBottom>
          Payment Method: <b>{post.settle_currency.method}</b>
        </Typography>
        <Typography variant="body2" gutterBottom>
          Contact: <b>{post.settle_currency.contact}</b>
        </Typography>
        <Typography variant="body2" gutterBottom>
          Expiry: <b>{expiry ? format(expiry, "yyyy-MM-dd, HH:mm") : "N/A"}</b>
        </Typography>
        <Stack sx={{ height: isMobile ? 40 : 0 }} />
        <Stack
          direction="row"
          gap={2}
          sx={{ position: "absolute", bottom: 16, right: 16 }}
        >
          {onAltButtonClick ? (
            <Tooltip title={altButtonDisabledText}>
              <Button
                sx={
                  altButtonDisabledText ? { cursor: "not-allowed" } : undefined
                }
                onClick={async () => {
                  if (altButtonDisabledText) {
                    return;
                  }
                  try {
                    setAltLoading(true);
                    const result = await onAltButtonClick();
                    setAltLoading(false);
                    return result;
                  } catch (err) {
                    setLoading(false);
                    throw err;
                  }
                }}
                variant="outlined"
                disabled={altLoading}
              >
                {altLoading ? (
                  <CircularProgress sx={{ mx: 1.5 }} size={25} />
                ) : (
                  altButtonText
                )}
              </Button>
            </Tooltip>
          ) : null}
          {onButtonClick ? (
            <Tooltip title={buttonDisabledText}>
              <Button
                sx={buttonDisabledText ? { cursor: "not-allowed" } : undefined}
                onClick={async () => {
                  if (buttonDisabledText) {
                    return;
                  }
                  try {
                    setLoading(true);
                    const result = await onButtonClick();
                    setLoading(false);
                    return result;
                  } catch (err) {
                    setLoading(false);
                    throw err;
                  }
                }}
                disabled={loading}
              >
                <StyledBadge badgeContent={buttonBadge} color="secondary">
                  {loading ? (
                    <CircularProgress sx={{ mx: 1.5 }} size={25} />
                  ) : (
                    buttonText || (!post?.is_dealer_buy ? "Buy" : "Sell")
                  )}
                </StyledBadge>
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  ) : null;
};

export default PostCard;
